import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';

export interface QuoteItem {
  id: string;
  productId?: string;
  productName: string;
  description?: string;
  premium: number;
  deductible?: number;
  coverageLimit?: number;
  quantity: number;
}

export interface Quote {
  id: string;
  quoteNumber: string;
  dealId?: string;
  dealName?: string;
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  status: QuoteStatus;
  items: QuoteItem[];
  subtotal: number;
  totalPremium: number;
  terms?: string;
  notes?: string;
  validUntil?: string;
  sentAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteFilters {
  status?: QuoteStatus;
  dealId?: string;
  organizationId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface QuotesResponse {
  quotes: Quote[];
  total: number;
  limit: number;
  offset: number;
}

// --- Helpers ---

function buildQueryString(filters?: QuoteFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.dealId) params.set('dealId', filters.dealId);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useQuotes(filters?: QuoteFilters) {
  return useQuery({
    queryKey: ['quotes', filters],
    queryFn: () => fetchJson<QuotesResponse>(`/api/quotes${buildQueryString(filters)}`),
  });
}

export function useQuote(id: string | null) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: () => fetchJson<Quote>(`/api/quotes/${id}`),
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Quote>) => postJson<Quote>('/api/quotes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

export function useUpdateQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Quote> }) =>
      putJson<Quote>(`/api/quotes/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', variables.id] });
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson<void>(`/api/quotes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}

export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postJson<Quote>(`/api/quotes/${id}/send`, {}),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
  });
}

export function useAcceptQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postJson<Quote>(`/api/quotes/${id}/accept`, {}),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
  });
}

export function useDeclineQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      postJson<Quote>(`/api/quotes/${id}/decline`, { reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', variables.id] });
    },
  });
}
